import React from 'react';

// Native React renditions of the public SMS legal pages, shown in the login-page
// modal. These intentionally do NOT depend on the server routes (/sms-optin,
// /sms-terms) being reachable: an iframe to those routes recursively renders the
// SPA (login page) whenever the server is stale/undeployed. The canonical,
// Twilio-visited pages are still the server routes in server/routes/legal.js;
// this is the same content for an in-page, always-works modal.

const PRIVACY_URL = 'https://www.rapidai.com/privacy-policy';
const LAST_UPDATED = 'October 19, 2026';

export function SmsTermsContent({ onShowPrivacy }) {
  return (
    <div style={styles.doc}>
      <h1 style={styles.h1}>RapidAI Internal SMS Program — Terms &amp; Conditions</h1>
      <p style={styles.meta}>
        Program operator: iSchemaView, Inc. (doing business as RapidAI) · Last updated: {LAST_UPDATED}
      </p>

      <h2 style={styles.h2}>1. Program description</h2>
      <p style={styles.p}>
        This SMS program sends operational status-update notifications to RapidAI sales
        representatives regarding the sales opportunities assigned to them. A representative
        receives a text requesting a status update on a specific opportunity and may reply by
        text with the update. The program is an application-to-person (A2P) workflow operated by
        RapidAI&rsquo;s internal software platform. It is used for operational purposes only and
        does not deliver marketing or promotional content.
      </p>

      <h2 style={styles.h2}>2. Consent / opt-in</h2>
      <p style={styles.p}>
        A representative may opt in either by (1) visiting this website&rsquo;s opt-in page,
        entering their mobile number, and checking the box agreeing to receive these messages;
        or (2) providing their mobile number and giving express consent during RapidAI
        onboarding, which a RapidAI administrator records. In both cases the representative
        receives a one-time confirmation text. No message is sent to any number before consent
        is obtained.
      </p>

      <h2 style={styles.h2}>3. Message frequency</h2>
      <p style={styles.p}>
        Message frequency varies based on the number and status of the opportunities assigned to
        a representative.
      </p>

      <h2 style={styles.h2}>4. Cost</h2>
      <p style={styles.p}>
        Message and data rates may apply according to the recipient&rsquo;s mobile carrier and
        plan. RapidAI does not charge for participation in this program.
      </p>

      <h2 style={styles.h2}>5. Opt-out (STOP)</h2>
      <p style={styles.p}>
        A recipient may opt out at any time by replying <code style={styles.code}>STOP</code> to
        any message. After replying <code style={styles.code}>STOP</code>, the recipient will
        receive one confirmation message and will not receive further messages from this program.
      </p>

      <h2 style={styles.h2}>6. Help (HELP)</h2>
      <p style={styles.p}>
        A recipient may reply <code style={styles.code}>HELP</code> at any time for assistance,
        or contact a Lyric administrator.
      </p>

      <h2 style={styles.h2}>7. Carriers</h2>
      <p style={styles.p}>Carriers are not liable for delayed or undelivered messages.</p>

      <h2 style={styles.h2}>8. Privacy</h2>
      <p style={styles.p}>
        Information collected through this program is handled in accordance with the RapidAI{' '}
        <a style={styles.a} href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" onClick={onShowPrivacy}>
          Privacy Policy
        </a>.
      </p>

      <h2 style={styles.h2}>9. Changes to these terms</h2>
      <p style={styles.p}>
        RapidAI may update these terms from time to time. Continued participation after an update
        constitutes acceptance of the revised terms.
      </p>

      <p style={styles.footer}>
        © iSchemaView, Inc. (RapidAI). This page describes an internal, operational SMS program
        and is published for message-program transparency.
      </p>
    </div>
  );
}

export function SmsOptInContent({ onShowTerms }) {
  const [status, setStatus] = React.useState('form'); // form | submitting | done
  const [error, setError] = React.useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fullName = form.fullName.value;
    const phone = form.phone.value.trim();
    const consent = form.consent.checked;

    if (!consent) { setError('You must check the consent box to opt in.'); return; }
    if (phone.replace(/\D/g, '').length < 10) { setError('Enter a valid mobile number.'); return; }

    setStatus('submitting');
    setError(null);
    try {
      const body = new URLSearchParams();
      body.set('fullName', fullName);
      body.set('phone', phone);
      body.set('consent', 'yes');
      const res = await fetch('/sms-optin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) { setStatus('done'); return; }
      setStatus('form');
      setError(
        res.status === 400
          ? 'That number could not be validated. Check it and try again.'
          : 'The opt-in service is not reachable right now. Please try again shortly.',
      );
    } catch {
      setStatus('form');
      setError('The opt-in service is not reachable right now. Please try again shortly.');
    }
  };

  if (status === 'done') {
    return (
      <div style={styles.doc}>
        <h1 style={styles.h1}>You&rsquo;re enrolled ✓</h1>
        <p style={styles.meta}>RapidAI SMS Program · iSchemaView, Inc.</p>
        <div style={styles.ok}>
          <p style={{ margin: '0 0 8px' }}>
            Thanks — your consent has been recorded and you&rsquo;ll receive a one-time
            confirmation text shortly.
          </p>
          <p style={{ margin: 0 }}>
            Message frequency varies. Message &amp; data rates may apply. Reply{' '}
            <code style={styles.code}>STOP</code> at any time to opt out, or{' '}
            <code style={styles.code}>HELP</code> for help.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.doc}>
      <h1 style={styles.h1}>RapidAI SMS Program — Opt-In</h1>
      <p style={styles.meta}>Program operator: iSchemaView, Inc. (doing business as RapidAI)</p>

      <p style={styles.p}>
        RapidAI sales representatives can enroll here to receive recurring{' '}
        <strong>operational</strong> text notifications about the Salesforce sales opportunities
        assigned to them (for example, a prompt to reply with a status update). This program does
        not send marketing or promotional messages.
      </p>

      {error && <div style={styles.err}>{error}</div>}

      <form onSubmit={submit}>
        <label style={styles.label} htmlFor="optin-fullName">Full name (optional)</label>
        <input style={styles.input} type="text" id="optin-fullName" name="fullName" autoComplete="name" />

        <label style={styles.label} htmlFor="optin-phone">Mobile number</label>
        <input
          style={styles.input}
          type="tel"
          id="optin-phone"
          name="phone"
          placeholder="+1 555 123 4567"
          required
          autoComplete="tel"
        />

        <label style={styles.consent}>
          <input type="checkbox" name="consent" value="yes" required style={{ marginTop: 5 }} />
          <span style={styles.consentText}>
            I agree to receive recurring operational SMS messages from RapidAI about my Salesforce
            opportunities. Message frequency varies. Message &amp; data rates may apply. Reply{' '}
            <code style={styles.code}>STOP</code> to opt out, <code style={styles.code}>HELP</code>{' '}
            for help. See the{' '}
            <a style={styles.a} href="/sms-terms" onClick={(ev) => { ev.preventDefault(); if (onShowTerms) onShowTerms(); }}>
              Terms &amp; Conditions
            </a>{' '}
            and{' '}
            <a style={styles.a} href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </span>
        </label>

        <button type="submit" style={styles.button} disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Signing up…' : 'Sign up for text updates'}
        </button>
        <p style={styles.note}>
          By submitting, you confirm the number entered is your own and that you consent to
          receive the messages described above.
        </p>
      </form>

      <p style={styles.footer}>
        © iSchemaView, Inc. (RapidAI). Questions? Contact a Lyric administrator.
      </p>
    </div>
  );
}

const styles = {
  doc: { color: '#1a2233', font: '15px/1.6 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' },
  h1: { fontSize: '22px', margin: '0 0 6px' },
  h2: { fontSize: '16px', margin: '24px 0 6px' },
  meta: { color: '#64748b', fontSize: '13px', margin: '0 0 8px' },
  p: { color: '#334155', margin: '0 0 10px' },
  a: { color: '#2563eb' },
  code: { background: '#f1f5f9', borderRadius: '4px', padding: '1px 5px', fontFamily: 'monospace' },
  label: { display: 'block', fontWeight: 600, margin: '16px 0 6px', fontSize: '14px' },
  input: { width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: '15px', border: '1px solid #cbd5e1', borderRadius: '6px', background: '#fff' },
  consent: { display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '20px' },
  consentText: { fontSize: '14px', color: '#334155' },
  button: { marginTop: '24px', padding: '11px 22px', fontSize: '15px', fontWeight: 600, color: '#fff', background: '#2563eb', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  note: { color: '#64748b', fontSize: '13px', marginTop: '10px' },
  ok: { background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', padding: '16px 18px', borderRadius: '8px' },
  err: { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '12px 14px', borderRadius: '8px', margin: '12px 0' },
  footer: { marginTop: '32px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', color: '#64748b', fontSize: '13px' },
};
